import { Box } from "@mui/material";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { BsArrowLeft } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";
import { postRegister } from "../redux/features/RegisterSlice";

const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState(false);

  const { isLoading } = useSelector((state) => state.register);
  const me = useSelector((state) => state.login.me);

  const initialValues = {
    fullName: "",
    email: "",
    phone: "",
    password: "",
    confirm_password: "",
    referral_code: "",
  };

  const validationSchema = Yup.object({
    fullName: Yup.string().required("Required"),
    email: Yup.string().email("Invalid email address").required("Required"),
    phone: Yup.string().required("Required"),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Required"),
    confirm_password: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Required"),
  });

  const onSubmit = (values, { resetForm }) => {
    const dataToSubmit = { ...values };
    delete dataToSubmit.referral_code;
    delete dataToSubmit.confirm_password;

    dispatch(postRegister(dataToSubmit))
      .unwrap()
      .then(() => {
        resetForm();
        navigate("/otp-page");
        // toast.success("Successfully Register!"); // Clear the form after successful registration
      })
      .catch((error) => {
        // Error handling is done through toast in slice, no need to do it here
      });
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPassword = () => {
    setConfirmPassword(!confirmPassword);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (me?.isActive) {
      navigate("/");
    }
  }, [me, navigate]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-no-repeat bg-cover bg-register_bg">
      <div className="p-6 bg-opacity-15 backdrop-blur-[1] bg-white shadow-lg rounded-xl my-gap md:w-[50rem] w-[90%] ">
        <div className="flex flex-col items-center justify-center gap-2">
          {/* <Avatar sx={{ width: 100, height: 100 }} /> */}
          <h2 className="text-3xl font-medium text-center text-white">
            Create a new account
          </h2>
        </div>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {() => (
            <Form>
              <div className="rounded-md shadow-sm">
                <Box
                  columnGap={2}
                  display="grid"
                  gridTemplateColumns={{
                    xs: "repeat(1, 1fr)",
                    sm: "repeat(2, 1fr)",
                  }}
                >
                  <div className="w-full">
                    <h2 className="mt-4 text-white">Name</h2>
                    <Field
                      id="fullName"
                      name="fullName"
                      type="text"
                      autoComplete="fullName"
                      placeholder="Full Name"
                      className="w-full px-3 py-3 duration-200 border border-gray-300 rounded-md hover:border-black"
                    />
                    <ErrorMessage
                      name="fullName"
                      component="div"
                      className="text-sm text-red-500"
                    />
                  </div>
                  <div>
                    <h2 className="mt-4 text-white">Email</h2>
                    <Field
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      placeholder="Email"
                      className="w-full px-3 py-3 duration-200 border border-gray-300 rounded-md hover:border-black"
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-sm text-red-500"
                    />
                  </div>
                  <div>
                    <h2 className="mt-4 text-white">Phone Number</h2>
                    <Field
                      id="phone"
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      placeholder="Phone Number"
                      className="w-full px-3 py-3 duration-200 border border-gray-300 rounded-md hover:border-black"
                    />
                    <ErrorMessage
                      name="phone"
                      component="div"
                      className="text-sm text-red-500"
                    />
                  </div>
                  <div>
                    <h2 className="mt-4 text-white">Password</h2>
                    <div className="relative">
                      <Field
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder="Password"
                        className="w-full px-3 py-3 duration-200 border border-gray-300 rounded-md hover:border-black"
                      />
                      <span
                        className="absolute transform -translate-y-1/2 cursor-pointer top-1/2 right-4"
                        onClick={toggleShowPassword}
                      >
                        {showPassword ? "Hide" : "Show"}
                      </span>
                    </div>
                    <ErrorMessage
                      name="password"
                      component="div"
                      className="text-sm text-red-500"
                    />
                  </div>
                  <div>
                    <h2 className="mt-4 text-white">Confirm Password</h2>
                    <div className="relative">
                      <Field
                        id="confirm_password"
                        name="confirm_password"
                        type={confirmPassword ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder="Confirm Password"
                        className="w-full px-3 py-3 duration-200 border border-gray-300 rounded-md hover:border-black"
                      />
                      <span
                        className="absolute transform -translate-y-1/2 cursor-pointer top-1/2 right-4"
                        onClick={toggleConfirmPassword}
                      >
                        {confirmPassword ? "Hide" : "Show"}
                      </span>
                    </div>
                    <ErrorMessage
                      name="confirm_password"
                      component="div"
                      className="text-sm text-red-500"
                    />
                  </div>
                </Box>
              </div>
              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full px-6 py-3 mt-5 text-white transition-all duration-500 bg-black border rounded-full hover:bg-white hover:text-black hover:border-black"
                >
                  {isLoading ? "Registering..." : "Register"}
                </button>
              </div>

              <div className="flex mt-4 text-sm">
                <p className="w-full font-medium text-center text-white">
                  Already have an account?
                  <Link
                    onClick={scrollToTop}
                    to="/login"
                    className="ml-2 text-white underline"
                  >
                    Login
                  </Link>
                </p>
              </div>
            </Form>
          )}
        </Formik>
        <div className="mt-4 text-sm text-center">
          <Link
            onClick={scrollToTop}
            to="/"
            className="flex items-center gap-2 font-medium text-white"
          >
            <BsArrowLeft /> Back to Home Page
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
